import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { Box, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { getDataAnalyticsToken } from '../../store/auth/api';
import { updateUserDetails } from '../../store/user/duck';
import { switchLanguage } from '../../utils/locale';
import { selectedLocale } from '../../store/locale/action';
import NativeOrWeb from '../../utils/native';
import { toggleDialog } from '../../store/control/duck';
import { goToAddClient, goToEditPortfolio } from '../../components/ClientEntry/utils';
import { FINANCIAL_CHARTS_MEDICAL_SAVING } from '../../constants/financial-charts';
import { updateViewingClient } from '../../store/client/duck';
import { setClientIdLocalStorage, goToPortfolio } from '../../components/ClientEntry/utils';
import {
  MEDICAL_RESERVE_DOM_READY,
  deliverMedicalReserveShare,
  subscribeMedicalReserveFiles
} from '../../utils/native/incoming-share-medical';

const dataAnalyticsOrigin = (() => {
  const url = process.env.DATA_ANALYTICS_FRONTEND_URL;
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch (error) {
    return url;
  }
})();

const DataAnalytics = ({
  intl,
  history,
  location,
  feature,
  clients,
  updateUserDetails,
  selectedLocale,
  toggleDialog,
  viewingClient,
  updateViewingClient
}) => {
  const isLargeScreen = useMediaQuery('(min-width:960px)');
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modified, setModified] = useState(false);
  const [factsheetId, setFactsheetId] = useState(null);
  const iframeRef = React.useRef(null);
  const iframeReadyRef = React.useRef(false);
  const deliverSharedFilesRef = React.useRef(() => {});

  // Extract category from URL query params
  const urlParams = new URLSearchParams(location.search);
  const category = urlParams.get('category');

  const updateFactsheetId = () => {
    let clientId = (localStorage.getItem('clientId') || '').replaceAll('"', '').replaceAll("'", '');
    if (clientId) {
      let client = clients.find(client => client._id === clientId);
      if (client) {
        setFactsheetId(client.factsheetId._id);
        return;
      }
    }
    setFactsheetId(null);
  };

  const fetchToken = async () => {
    try {
      setLoading(true);
      const response = await getDataAnalyticsToken();
      setToken(response.token);
    } catch (error) {
      console.error('Error fetching token:', error);
    } finally {
      setLoading(false);
    }
  };

  const postMessageToIframe = (messageType, data = {}) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        {
          type: messageType,
          ...data
        },
        process.env.DATA_ANALYTICS_FRONTEND_URL
      );
    }
  };

  useEffect(() => {
    iframeReadyRef.current = false;
    updateFactsheetId();
    fetchToken();
  }, [feature, location.pathname]);

  deliverSharedFilesRef.current = () => {
    if (feature !== 'medical-reserve') return;
    deliverMedicalReserveShare(iframeReadyRef.current, message =>
      postMessageToIframe(message.type, { files: message.files })
    );
  };

  useEffect(() => {
    iframeReadyRef.current = false;
    if (feature !== 'medical-reserve') return undefined;
    return subscribeMedicalReserveFiles(() => deliverSharedFilesRef.current());
  }, [feature, token]);

  useEffect(() => {
    const handleMessage = async event => {
      if (event.data?.type === 'authenticate') {
        updateFactsheetId();
        fetchToken();
      } else if (event.data?.type === 'changeLanguage') {
        let language = event.data.language === 'hk' ? 'zh-Hant-HK' : event.data.language === 'cn' ? 'zh' : 'en';
        updateUserDetails({ language });
        switchLanguage(selectedLocale, language);
      } else if (event.data?.type === 'downloadFile') {
        const { blob, fileName, fileType } = event.data;
        await NativeOrWeb.downloadFile(blob, fileName, fileType);
      } else if (event.data?.type === 'openFinancialConcept') {
        let defaultItem = { ...FINANCIAL_CHARTS_MEDICAL_SAVING };
        toggleDialog('financialCharts', { defaultItem });
      } else if (event.data?.type === 'addClient') {
        goToAddClient(history);
      } else if (event.data?.type === 'viewPolicy') {
        // const { policy } = event.data;
        // goToEditPortfolio(history, policy);
        goToPortfolio(viewingClient, history, true, {});
      } else if (event.data?.type === 'dataChanged') {
        setModified(event.data.modified && Boolean(event.data.selectedFactsheet));
        if (event.data.selectedFactsheet) {
          let client = clients.find(client => client.factsheetId._id === event.data.selectedFactsheet._id);
          if (client) {
            updateViewingClient(client);
            setClientIdLocalStorage(client._id);
          }
        }
      } else if (event.data?.type === 'push') {
        console.log('message got -> push to::: ', event.data.path);
        history.push(event.data.path);
      } else if (event.data?.type === MEDICAL_RESERVE_DOM_READY) {
        // The medical-reserve embed sends this once its document can accept files.
        // Reply is { type: 'incomingShare', files: [{ name, mimeType, blob }] }.
        if (dataAnalyticsOrigin && event.origin !== dataAnalyticsOrigin) return;
        iframeReadyRef.current = true;
        deliverSharedFilesRef.current();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [updateUserDetails, selectedLocale, clients, viewingClient]);

  // Block navigation when modified is true
  useEffect(() => {
    const unblock = history.block(location => {
      if (modified && location.pathname !== history.location.pathname) {
        postMessageToIframe('navigationAttempted');
        return false;
      }
      return true;
    });

    return () => {
      unblock();
    };
  }, [modified, history]);

  if (loading || !token) {
    return (
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '16px',
          alignItems: 'center',
          width: '100%',
          height: '100%'
        }}
      >
        {loading ? (
          <>
            <CircularProgress color="primary" size={36} />
          </>
        ) : (
          <Typography style={{ fontSize: 16, textAlign: 'center' }}>
            {intl.formatMessage({ id: 'error-msg' })}
          </Typography>
        )}
      </Box>
    );
  }

  let language = intl.locale === 'zh-Hant-HK' ? 'hk' : intl.locale === 'zh' ? 'cn' : 'en';

  const featurePath = feature === 'add-policy' ? 'client' : feature;
  const iframeSrc = `${
    process.env.DATA_ANALYTICS_FRONTEND_URL
  }/embed/${featurePath}?token=${token}&language=${language}${factsheetId ? `&factsheet=${factsheetId}` : ''}${
    category ? `&category=${category}` : ''
  }`;

  return (
    <Box style={{ width: '100%', height: '100%', overflow: 'clip' }}>
      <iframe
        ref={iframeRef}
        allow="clipboard-read; clipboard-write; camera; microphone; geolocation; fullscreen; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        src={iframeSrc}
        style={{
          width: '100%',
          height: isLargeScreen ? '100%' : 'calc(100% - var(--bottom-padding, 0))',
          border: 'none',
          paddingTop: 45,
          paddingBottom: isLargeScreen ? 0 : 44
        }}
      />
    </Box>
  );
};

const container = connect(
  state => ({
    clients: state.client.clientData,
    selectedLocale: state.locale.selectedLocale,
    viewingClient: state.client.viewingClient
  }),
  {
    updateUserDetails,
    selectedLocale,
    toggleDialog,
    updateViewingClient
  }
)(DataAnalytics);

export default injectIntl(withRouter(container));
